import { withAlpha, type Color } from "../../common/color.js";
import { Container, type ContainerOptions } from "../core/Container.js";
import { clamp, type Point } from "../../common/geometry.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import { StatBar } from "./StatBar.js";

export interface NameplateOptions extends ContainerOptions {
  text?: string;
  /** Color of the name: faction, hostility, party. */
  nameColor?: Color | null;
  font?: FontSpec | null;
  /** Show a health bar under the name. */
  showBar?: boolean;
  health?: number;
  maxHealth?: number;
  barHeight?: number;
  /** Distance from the camera, for fading and sorting. */
  distance?: number;
  /** Fully faded out past this distance. 0 never fades. */
  fadeDistance?: number;
  /** Hidden entirely past this distance. 0 never hides. */
  cullDistance?: number;
}

const DEFAULT_BAR_HEIGHT = 5;
/** Portion of the fade distance over which it fades, rather than snapping. */
const FADE_BAND = 0.3;

/**
 * A name and health bar that tracks something in the world.
 *
 * The 3D is not Webwin's business: the game projects a world point to a screen
 * point and calls `placeAt`, which centers the plate on it. What Webwin
 * contributes is everything after that — measuring the name, sizing the plate
 * to it, fading it out with distance, and `depthSort`, which puts the near
 * plates in front of the far ones so a crowd does not draw in an arbitrary
 * order every frame.
 */
export class Nameplate extends Container {
  #text: string;
  #bar: StatBar | null = null;

  nameColor: Color | null;
  font: FontSpec | null;
  barHeight: number;
  distance: number;
  fadeDistance: number;
  cullDistance: number;

  constructor(options: NameplateOptions = {}) {
    super({ width: 120, height: 28, tabStop: false, ...options });
    this.#text = options.text ?? "";
    this.nameColor = options.nameColor ?? null;
    this.font = options.font ?? null;
    this.barHeight = options.barHeight ?? DEFAULT_BAR_HEIGHT;
    this.distance = options.distance ?? 0;
    this.fadeDistance = options.fadeDistance ?? 0;
    this.cullDistance = options.cullDistance ?? 0;

    if (options.showBar ?? true) {
      this.#bar = new StatBar({
        value: options.health ?? 100,
        maximum: options.maxHealth ?? 100,
        height: this.barHeight,
        // A plate in a crowd is small and moving; the trailing fill would be
        // noise at that size. Turn it on per plate for a boss bar.
        chipSeconds: 0,
      });
      this.controls.add(this.#bar);
    }
  }

  get text(): string {
    return this.#text;
  }
  set text(v: string) {
    if (this.#text === v) return;
    this.#text = v;
    this.invalidate();
  }

  /** The health bar, or null when the plate was built without one. */
  get bar(): StatBar | null {
    return this.#bar;
  }

  get health(): number {
    return this.#bar?.value ?? 0;
  }
  set health(v: number) {
    if (this.#bar) this.#bar.value = v;
  }

  get maxHealth(): number {
    return this.#bar?.maximum ?? 0;
  }
  set maxHealth(v: number) {
    if (this.#bar) this.#bar.maximum = v;
  }

  /**
   * How visible the plate is at its current distance, 0..1.
   *
   * Fading over a band rather than at a line: plates popping in and out as a
   * player walks is far more distracting than plates that are slightly dim.
   */
  get opacity(): number {
    if (this.cullDistance > 0 && this.distance >= this.cullDistance) return 0;
    if (this.fadeDistance <= 0) return 1;
    const band = this.fadeDistance * FADE_BAND;
    if (this.distance <= this.fadeDistance - band) return 1;
    return clamp((this.fadeDistance - this.distance) / band + 1, 0, 1);
  }

  /**
   * Center the plate on a screen point, in the parent's coordinates.
   *
   * `screen` is where the thing is; the plate sits above it by its own height
   * so the name is over the head rather than through it.
   */
  placeAt(screen: Point): void {
    this.setLocation(Math.round(screen.x - this.width / 2), Math.round(screen.y - this.height));
  }

  /** Size to fit the name, which is what makes a row of plates line up. */
  measureTo(rc: RenderContext, theme: Theme): void {
    const font = this.font ?? theme.metrics.font;
    const width = Math.max(60, Math.ceil(rc.measureText(this.#text, font).width) + 8);
    const lineHeight = Math.ceil(rc.lineHeight(font));
    const height = lineHeight + (this.#bar ? this.barHeight + 2 : 0);
    if (width !== this.width || height !== this.height) this.setSize(width, height);
    if (this.#bar) this.#bar.setBounds(0, lineHeight + 2, width, this.barHeight);
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    this.measureTo(rc, theme);
    const alpha = this.opacity;
    if (alpha <= 0) return;

    theme.drawNameplateFrame(rc, this.localRect, this.visualState());

    const font = this.font ?? theme.metrics.font;
    const ink = withAlpha(this.nameColor ?? theme.palette.text, alpha);
    const width = rc.measureText(this.#text, font).width;
    rc.drawText(this.#text, (this.width - width) / 2, 0, font, ink, { baseline: "top" });
  }
}

/**
 * Order plates so the near ones draw last, and hide the culled ones.
 *
 * Call it once a frame after updating distances. Reordering the actual
 * children is what makes overlapping plates stable: without it, two plates
 * that cross swap which is on top depending on insertion order, which reads
 * as flicker.
 */
export function depthSort(plates: readonly Nameplate[]): Nameplate[] {
  const visible = plates.filter((p) => p.opacity > 0);
  // Descending distance: furthest painted first, nearest painted over it.
  return [...visible].sort((a, b) => b.distance - a.distance);
}
